import CustomerStats from "./CustomerStats";
import { formatWalletMovement } from "./walletPresentation";
import React, { useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import ChatIcon from "@material-ui/icons/Chat";
import EditIcon from "@material-ui/icons/Edit";
import ShoppingCartIcon from "@material-ui/icons/ShoppingCart";
import PersonOutlinedIcon from "@material-ui/icons/PersonOutlined";
import StorefrontIcon from "@material-ui/icons/Storefront";
import StarsIcon from "@material-ui/icons/Stars";
import AccountBalanceWalletIcon from "@material-ui/icons/AccountBalanceWallet";
import SendIcon from "@material-ui/icons/Send";
import DevicesIcon from "@material-ui/icons/Devices";
import AlternateEmailIcon from "@material-ui/icons/AlternateEmail";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import VpnKeyIcon from "@material-ui/icons/VpnKey";
import { useDialogStyles } from "../../theme/redesign";
import { useCustomerChat } from "./CustomerChatButton";
import {
  RecordCard,
  RecordsCardsGrid,
  useViewMode,
  ViewModeToggle,
} from "../../components/ViewModeToggle";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { openBinancePaymentFromNotify } from "./BinanceCheckoutDialog";
import { toast } from "react-toastify";
import { accountUsesPin, formatDate, formatDateTime, formatWallet, getNegocioFormat, looksLikePhone, matchesQuery, money, parseCustomerCsv, pointsSystemOn, readQuery, servicesPath, storedPhone, visibleValue } from "./helpers";
import { ContactAvatar, EmptyHint, FilterChips, LoadingHint, NegocioPager, PageFrame, PhoneFields, PhoneWithFlag, SearchField, StatusChip, TrafficLight, DueBadge, stopRow, tNegocio, useMobile, usePager } from "./shared";
import { AccountPeek, ProductMark, SalePeek } from "./AccountPeek";
import ConfirmationModal from "../../components/ConfirmationModal";
import { AddCartItemDialog, CartConfigDialog, PurchasedItemDialog } from "./CartDialog";
import { CustomerServiceDialog, PointsSendTrigger, SendPromptDialog, ServiceRowActions } from "./CustomerServiceDialog";
import { bumpNegocio, useRecords } from "./Lists";
import { useStyles } from "./styles";
import CustomerStoreAccess from "./CustomerStoreAccess";

const KIND_STATUS = {
  active: "active",
  expiring: "expiring",
  expired: "expired",
  none: "none",
};

const emptyForm = () => ({
  name: "",
  number: "",
  notes: "",
  telegram: "",
  customerType: "final",
  pointsEnabled: true,
});

const typeLabel = (value) =>
  tNegocio(value === "reseller" ? "pages.customers.types.reseller" : "pages.customers.types.final");

function ServiceAccountCell({ sub }) {
  const classes = useStyles();
  const account = sub?.account || {};
  const profile = sub?.profile;
  const email = visibleValue(account.email) || visibleValue(account.code);
  const password = visibleValue(account.password);
  const pin = visibleValue(profile?.pin) && (accountUsesPin(account) || profile?.pin)
    ? profile.pin
    : "";
  return (
    <div className={classes.serviceAccountCell}>
      <span className={classes.optionRow}>
        <ProductMark product={sub?.product} size={22} circle />
        <span className={classes.serviceAccountEmail}>{email || sub?.product?.name || "—"}</span>
      </span>
      {password ? (
        <span className={classes.accountPassword}>
          <VpnKeyIcon style={{ fontSize: 14 }} />
          {password}
        </span>
      ) : null}
      {profile?.name || pin ? (
        <span className={classes.captionBlock}>
          {[profile?.name, pin ? `PIN ${pin}` : null].filter(Boolean).join(" · ")}
        </span>
      ) : null}
    </div>
  );
}

export default function CustomersPage() {
  const classes = useStyles();
  const dlg = useDialogStyles();
  const isMobile = useMobile();
  const history = useHistory();
  const location = useLocation();
  const [viewMode, setViewMode] = useViewMode("negocio-customers");
  const { records, loading, reload } = useRecords("customers");
  const [query, setQuery] = useState("");
  const [kind, setKind] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [dialog, setDialog] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const { openChat, modal: chatModal } = useCustomerChat();
  const [peekAccountId, setPeekAccountId] = useState(null);
  const [peekSale, setPeekSale] = useState(null);
  const [peekItem, setPeekItem] = useState(null);
  const [pointsInfo, setPointsInfo] = useState(null);
  const walletRequestRef = useRef(0);
  const [pointsRows, setPointsRows] = useState([]);
  const [pointsLoading, setPointsLoading] = useState(false);
  const [serviceDialog, setServiceDialog] = useState(null);
  const [sendPrompt, setSendPrompt] = useState(null);
  const batchSinceRef = useRef(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [salePrompt, setSalePrompt] = useState(null);
  const [cartConfig, setCartConfig] = useState(null);
  const [addProductPrompt, setAddProductPrompt] = useState(null);
  const [addItemCustomer, setAddItemCustomer] = useState(null);
  const payments = useRecords("payment-methods");

  React.useEffect(() => {
    const params = readQuery(location.search);
    if (params.contactId) openDetail(Number(params.contactId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const rows = useMemo(() => {
    const list = Array.isArray(records) ? records : [];
    return list.filter(
        (c) =>
          (!kind || c.kind === kind) &&
          (!typeFilter || c.customerType === typeFilter) &&
          matchesQuery(c, query, [
            c.name,
            c.number,
            c.telegram,
            c.code,
            c.countryName,
            c.countryCode,
          ])
      );
  }, [records, query, kind, typeFilter]);
  const pager = usePager(rows);
  const servicePager = usePager(detail?.subscriptions || [], 10);
  const salePager = usePager(detail?.sales || [], 10);
  const walletPager = usePager(pointsRows, 10);

  const openDetail = async (contactId) => {
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/negocio/customers/${contactId}`);
      setDetail(data);
    } catch (e) {
      toastError(e);
    } finally {
      setDetailLoading(false);
    }
  };

  const saveCustomer = async () => {
    if (!dialog) return;
    if (!String(dialog.name || "").trim()) {
      toast.error(tNegocio("pages.customers.nameRequired") || tNegocio("fields.name"));
      return;
    }
    const hasPhone = looksLikePhone(dialog.number);
    const hasTelegram = String(dialog.telegram || "").replace(/^@+/, "").trim();
    if (!hasPhone && !hasTelegram) {
      toast.error(tNegocio("pages.customers.phoneOrTelegram"));
      return;
    }
    try {
      const { data } = await api.post("/negocio/customers", { ...dialog, number: storedPhone(dialog.number) });
      const wasNew = !dialog.contactId;
      toast.success(tNegocio("toastSaved"));
      setDialog(null);
      bumpNegocio("customers");
      reload({ soft: true });
      if (wasNew && data) setSalePrompt(data);
    } catch (e) {
      toastError(e);
    }
  };

  const startEdit = (row) => {
    setDialog({
      contactId: row.contactId,
      name: row.name || "",
      number: row.number || "",
      notes: row.notes || "",
      telegram: row.telegram || "",
      customerType: row.customerType || "final",
      discountPercent: row.discountPercent ?? "",
      pointsEnabled: row.pointsEnabled !== false,
      autoRenewWallet: !!row.autoRenewWallet,
      walletCurrency: row.walletCurrency || "",
      balanceNow: Number(row.walletBalance || 0),
      balanceCurrency: row.walletCurrency || "",
    });
  };

  const walletConversionHint = () => {
    const { currency: local, currencyFx: fx, exchangeRate } = getNegocioFormat();
    const from = dialog?.balanceCurrency;
    const to = dialog?.walletCurrency;
    const rate = Number(exchangeRate) > 0 ? Number(exchangeRate) : 1;
    if (!dialog?.contactId || !from || !to || from === to || !(Math.abs(dialog.balanceNow) > 0.001)) return "";
    const converted = to === local ? dialog.balanceNow * rate : from === local ? dialog.balanceNow / rate : dialog.balanceNow;
    return tNegocio("pages.customers.walletCurrencyConvert")
      .replace("{{from}}", formatWallet(dialog.balanceNow, from))
      .replace("{{to}}", formatWallet(converted, to === fx || to === local ? to : fx))
      .replace("{{rate}}", String(rate));
  };

  const openTicket = (row) => openChat(row);

  const openPoints = async (row, e) => {
    if (e) e.stopPropagation();
    if (!row?.contactId) return;
    const requestId = ++walletRequestRef.current;
    setPointsInfo(row);
    setPointsRows(Array.isArray(row.walletMovements) ? row.walletMovements : []);
    setPointsLoading(true);
    try {
      const { data } = await api.get(`/negocio/customers/${row.contactId}`);
      if (walletRequestRef.current !== requestId) return;
      setPointsInfo((prev) => ({ ...(prev || {}), ...data }));
      setPointsRows(Array.isArray(data?.walletMovements) ? data.walletMovements : []);
    } catch (err) {
      if (walletRequestRef.current === requestId) toastError(err);
    } finally {
      if (walletRequestRef.current === requestId) setPointsLoading(false);
    }
  };

  const closePoints = () => { walletRequestRef.current += 1; setPointsInfo(null); setPointsRows([]); setPointsLoading(false); };

  const notifyCart = async ({ kind, channel, contactId, sinceUnix, subscriptionIds }) => {
    try {
      const { data } = await api.post("/negocio/notify", {
        notify: true,
        cart: true,
        kind,
        channel,
        contactId,
        ...(sinceUnix != null ? { sinceUnix } : {}),
        ...(subscriptionIds?.length ? { subscriptionIds } : {}),
      });
      toast.success(tNegocio("pages.customers.sentOk"));
      openBinancePaymentFromNotify(data);
    } catch (e) {
      toastError(e);
    }
  };

  const openSendLast = (customer, subscriptionIds = []) => {
    if (!customer) return;
    if (batchSinceRef.current == null) batchSinceRef.current = { contactId: customer.contactId, ids: [] };
    const batch = batchSinceRef.current;
    batch.ids = [...new Set([...batch.ids, ...subscriptionIds])];
    setSendPrompt({ kind: "last", customer, cart: true, subscriptionIds: batch.ids });
  };

  const closeSendPrompt = () => {
    setSendPrompt(null);
    batchSinceRef.current = null;
  };

  const notifyCustomer = async ({ kind, channel, contactId, subscriptionId }) => {
    try {
      const { data } = await api.post("/negocio/notify", {
        notify: true,
        kind,
        channel,
        contactId,
        subscriptionId,
      });
      toast.success(tNegocio("pages.customers.sentOk"));
      openBinancePaymentFromNotify(data);
      if (kind === "update" && detail?.contactId) openDetail(detail.contactId);
    } catch (e) {
      toastError(e);
    }
  };

  const onServiceSaved = async (mode, sub) => {
    setServiceDialog(null);
    if (detail?.contactId) await openDetail(detail.contactId);
    reload({ soft: true });
    if (mode === "renew") {
      setSendPrompt({
        kind: "renew",
        customer: detail,
        subscriptionId: sub?.id,
      });
    }
  };

  const renderActions = (c) => (
    <div className={classes.actionCluster}>
      <Tooltip title={tNegocio("pages.customers.openChat")}>
        <IconButton size="small" onClick={stopRow(() => openTicket(c))}>
          <ChatIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={tNegocio("edit")}>
        <IconButton size="small" onClick={stopRow(() => startEdit(c))}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={tNegocio("new.sale")}>
        <IconButton size="small" onClick={stopRow(() => history.push(servicesPath(c.contactId, true)))}>
          <ShoppingCartIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </div>
  );

  const codeClass = (c) =>
    `${classes.customerCode} ${c?.customerType === "reseller" ? classes.codeReseller : classes.codeFinal}`;

  const renderCode = (c) => (c?.code ? <span className={codeClass(c)}>{c.code}</span> : null);

  const renderType = (c) => {
    const reseller = c?.customerType === "reseller";
    return (
      <Tooltip title={typeLabel(c?.customerType)}>
        <span className={`${classes.typeMark} ${reseller ? classes.typeReseller : classes.typeFinal}`}>
          {reseller ? <StorefrontIcon /> : <PersonOutlinedIcon />}
        </span>
      </Tooltip>
    );
  };

  const renderPoints = (c) => (
    <div
      className={`${classes.metricClick} ${classes.metricCell} ${Number(c.walletBalance) ? "" : classes.metricMuted}`}
      onClick={(e) => openPoints(c, e)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") openPoints(c, e);
      }}
    >
      <AccountBalanceWalletIcon fontSize="small" className={classes.metricPoints} />
      {formatWallet(c.walletBalance, c.walletCurrency)}
    </div>
  );

  const renderIdentity = (c) => (
    <div className={classes.agendaCell}>
      <ContactAvatar name={c.name} number={c.number} profilePicUrl={c.profilePicUrl} />
      <div className={classes.agendaBody}>
        <div style={{ fontWeight: 700 }}>{c.name}</div>
      </div>
    </div>
  );

  const renderPhone = (c) => (
    <PhoneWithFlag number={c?.number} countryCode={c?.countryCode} countryName={c?.countryName} />
  );

  const runImport = async () => {
    if (!importFile) {
      toast.error(tNegocio("pages.customers.import.pickFile"));
      return;
    }
    if (importFile.size > 5 * 1024 * 1024) {
      toast.error(tNegocio("pages.customers.import.maxSize"));
      return;
    }
    setImporting(true);
    try {
      const text = await importFile.text();
      const rows = parseCustomerCsv(text);
      if (!rows.length) {
        toast.error(tNegocio("pages.customers.import.empty"));
        return;
      }
      const { data } = await api.post("/negocio/customers/import", { rows, sync: true });
      toast.success(
        tNegocio("pages.customers.import.done")
          .replace("{created}", String(data?.created || 0))
          .replace("{skipped}", String(data?.skipped || 0))
      );
      setImportOpen(false);
      setImportFile(null);
      reload({ soft: true });
    } catch (e) {
      toastError(e);
    } finally {
      setImporting(false);
    }
  };

  return (
    <>
      <PageFrame
        lockBody
        title={tNegocio("pages.customers.title")}
        subtitle={tNegocio("pages.customers.agendaSubtitle")}
        actions={
          <>
            <ViewModeToggle value={viewMode} onChange={setViewMode} />
            <Button startIcon={<CloudUploadIcon />} onClick={() => { setImportFile(null); setImportOpen(true); }}>
              {tNegocio("pages.customers.import.action")}
            </Button>
            <Button variant="contained" color="primary" onClick={() => setDialog(emptyForm())}>
              {tNegocio("new.customer")}
            </Button>
          </>
        }
      >
        <div className={classes.toolbar}>
          <div className={classes.filterRow}>
            <FilterChips
              value={typeFilter}
              onChange={setTypeFilter}
              options={[
                { value: "", label: tNegocio("filters.all") },
                {
                  value: "final",
                  label: tNegocio("pages.customers.types.final"),
                  icon: <PersonOutlinedIcon />,
                },
                {
                  value: "reseller",
                  label: tNegocio("pages.customers.types.reseller"),
                  icon: <StorefrontIcon />,
                },
              ]}
            />
            <span className={classes.filterSplit} />
            <FilterChips
              value={kind}
              onChange={setKind}
              options={[
                { value: "", label: tNegocio("filters.all") },
                { value: "active", label: tNegocio("pages.customers.kinds.active") },
                { value: "expiring", label: tNegocio("pages.customers.kinds.expiring") },
                { value: "expired", label: tNegocio("pages.customers.kinds.expired") },
                { value: "none", label: tNegocio("pages.customers.kinds.none") },
              ]}
            />
          </div>
          <SearchField value={query} onChange={setQuery} />
        </div>

        {rows.length && viewMode === "cards" ? (
          <>
          <div className={classes.listPane}>
          <RecordsCardsGrid>
            {pager.slice.map((c) => (
              <RecordCard
                key={c.contactId}
                onClick={() => openDetail(c.contactId)}
                actions={isMobile ? null : renderActions(c)}
              >
                <div className={classes.cardHead}>
                  <div className={classes.agendaCell} style={isMobile ? { gap: 10, flex: 1 } : undefined}>
                    <ContactAvatar name={c.name} number={c.number} profilePicUrl={c.profilePicUrl} size={isMobile ? 40 : 48} />
                    <div className={classes.agendaBody}>
                      <div className={classes.cellEllipsis} style={{ fontWeight: 800 }}>{c.name}</div>
                      <div className={classes.contactLine}>
                        {renderPhone(c) || <span>{c.number || ""}</span>}
                        {c.telegram ? <span>@{c.telegram}</span> : null}
                      </div>
                    </div>
                  </div>
                  <div className={classes.cardBadges}>
                    {renderType(c)}
                    {renderCode(c)}
                    <TrafficLight kind={KIND_STATUS[c.kind] || "none"} />
                  </div>
                </div>
                <div className={classes.cardMetrics}>
                  {renderPoints(c)}
                  <div className={classes.metricCell}>
                    <DevicesIcon fontSize="small" className={classes.metricServices} />
                    <span aria-label={`${tNegocio("pages.customers.active")}: ${c.activeSubs}`}>{c.activeSubs}{!isMobile ? ` ${tNegocio("pages.customers.active")}` : ""}</span>
                  </div>
                  <div className={classes.metricCell}>
                    <AttachMoneyIcon fontSize="small" className={classes.metricMoney} />
                    {money(c.totalPaid)}
                  </div>
                </div>
                {isMobile ? <div className={classes.customerCardActions} onClick={(event) => event.stopPropagation()}>{renderActions(c)}</div> : null}
              </RecordCard>
            ))}
          </RecordsCardsGrid>
          </div>
          <NegocioPager
            count={pager.count}
            page={pager.page}
            rowsPerPage={pager.rowsPerPage}
            onChangePage={pager.setPage}
            onChangeRowsPerPage={pager.setRowsPerPage}
          />
          </>
        ) : rows.length ? (
          <div className={`${classes.tableWrap} ${classes.tableWrapFill}`}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{tNegocio("cols.code")}</TableCell>
                  <TableCell>{tNegocio("cols.type")}</TableCell>
                  <TableCell>{tNegocio("cols.contact")}</TableCell>
                  <TableCell>{tNegocio("cols.phone")}</TableCell>
                  <TableCell>
                    <span className={classes.colHead}>
                      <AlternateEmailIcon />
                      {tNegocio("cols.telegram")}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={classes.colHead}>
                      <AccountBalanceWalletIcon className={classes.metricPoints} />
                      {tNegocio("pages.customers.wallet")}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={classes.colHead}>
                      <DevicesIcon className={classes.metricServices} />
                      {tNegocio("pages.customers.active")}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={classes.colHead}>
                      <AttachMoneyIcon className={classes.metricMoney} />
                      {tNegocio("pages.customers.totalPurchases")}
                    </span>
                  </TableCell>
                  <TableCell>{tNegocio("cols.status")}</TableCell>
                  <TableCell className={classes.stickyActions} />
                </TableRow>
              </TableHead>
              <TableBody>
                {pager.slice.map((c) => (
                  <TableRow key={c.contactId} hover className={classes.clickableRow} onClick={() => openDetail(c.contactId)}>
                    <TableCell>{renderCode(c)}</TableCell>
                    <TableCell>{renderType(c)}</TableCell>
                    <TableCell>{renderIdentity(c)}</TableCell>
                    <TableCell>{renderPhone(c) || "—"}</TableCell>
                    <TableCell>
                      {c.telegram ? (
                        <span className={classes.metricCell}>
                          <AlternateEmailIcon fontSize="small" className={classes.metricTelegram} />
                          {c.telegram}
                        </span>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      {renderPoints(c)}
                    </TableCell>
                    <TableCell>
                      <span className={`${classes.metricCell} ${c.activeSubs ? "" : classes.metricMuted}`}>
                        <DevicesIcon fontSize="small" className={classes.metricServices} />
                        {c.activeSubs}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={classes.metricCell}>
                        <AttachMoneyIcon fontSize="small" className={classes.metricMoney} />
                        {money(c.totalPaid)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <TrafficLight kind={KIND_STATUS[c.kind] || "none"} />
                    </TableCell>
                    <TableCell align="right" className={classes.stickyActions} onClick={(e) => e.stopPropagation()}>
                      {renderActions(c)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <NegocioPager
              count={pager.count}
              page={pager.page}
              rowsPerPage={pager.rowsPerPage}
              onChangePage={pager.setPage}
              onChangeRowsPerPage={pager.setRowsPerPage}
            />
          </div>
        ) : loading ? (
          <LoadingHint />
        ) : (
          <EmptyHint
            title={tNegocio("pages.customers.emptyTitle")}
            subtitle={tNegocio("pages.customers.empty")}
          />
        )}
      </PageFrame>

      <Dialog open={!!dialog} onClose={() => setDialog(null)} maxWidth="sm" fullWidth scroll="paper" classes={{ paper: dlg.paper }}>
        <DialogTitle>{dialog?.contactId ? tNegocio("edit") : tNegocio("new.customer")}</DialogTitle>
        <DialogContent dividers>
          <div className={classes.typeChoiceLabel}>{tNegocio("cols.type")}</div>
          <div className={classes.typeChoiceRow}>
            <button
              type="button"
              className={`${classes.typeChoice} ${(dialog?.customerType || "final") === "final" ? classes.typeChoiceOn : ""}`}
              onClick={() => setDialog((d) => ({ ...d, customerType: "final" }))}
            >
              <PersonOutlinedIcon />
              {tNegocio("pages.customers.types.final")}
            </button>
            <button
              type="button"
              className={`${classes.typeChoice} ${dialog?.customerType === "reseller" ? classes.typeChoiceOn : ""}`}
              onClick={() => setDialog((d) => ({ ...d, customerType: "reseller" }))}
            >
              <StorefrontIcon />
              {tNegocio("pages.customers.types.reseller")}
            </button>
            {pointsSystemOn() ? (
              <button
                type="button"
                className={`${classes.typeChoice} ${dialog?.pointsEnabled !== false ? classes.typeChoiceOn : ""}`}
                onClick={() => setDialog((d) => ({ ...d, pointsEnabled: d.pointsEnabled === false }))}
              >
                <StarsIcon className={dialog?.pointsEnabled !== false ? classes.metricPoints : undefined} />
                {dialog?.pointsEnabled !== false
                  ? tNegocio("pages.customers.pointsOn")
                  : tNegocio("pages.customers.pointsOff")}
              </button>
            ) : null}
          </div>
          {dialog?.customerType === "reseller" ? (
            <TextField
              fullWidth
              margin="dense"
              variant="outlined"
              type="number"
              inputProps={{ min: 0, max: 90, step: 0.5 }}
              label={tNegocio("pages.customers.discountPercent")}
              helperText={tNegocio("pages.customers.discountHint")}
              value={dialog?.discountPercent ?? ""}
              onChange={(e) => setDialog((d) => ({ ...d, discountPercent: e.target.value }))}
            />
          ) : null}
          {pointsSystemOn() && dialog?.pointsEnabled !== false ? (
            <>
              <div className={classes.typeChoiceLabel}>{tNegocio("pages.customers.walletCurrency")}</div>
              <div className={classes.typeChoiceRow}>
                {[...new Set([getNegocioFormat().currency, getNegocioFormat().currencyFx])]
                  .filter(Boolean)
                  .map((code) => (
                    <button
                      key={code}
                      type="button"
                      className={`${classes.typeChoice} ${dialog?.walletCurrency === code ? classes.typeChoiceOn : ""}`}
                      onClick={() => setDialog((d) => ({ ...d, walletCurrency: code }))}
                    >
                      {code}
                    </button>
                  ))}
              </div>
              <Typography variant="caption" color="textSecondary" component="div">
                {walletConversionHint() || tNegocio("pages.customers.walletCurrencyHint")}
              </Typography>
              {dialog?.contactId ? (
                <div className={classes.typeChoiceRow} style={{ marginTop: 8 }}>
                  <button
                    type="button"
                    className={`${classes.typeChoice} ${dialog?.autoRenewWallet ? classes.typeChoiceOn : ""}`}
                    onClick={() => setDialog((d) => ({ ...d, autoRenewWallet: !d.autoRenewWallet }))}
                  >
                    🔄 {dialog?.autoRenewWallet ? tNegocio("pages.customers.autoRenewOn") : tNegocio("pages.customers.autoRenewOff")}
                  </button>
                </div>
              ) : null}
            </>
          ) : null}
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            label={tNegocio("fields.name")}
            value={dialog?.name || ""}
            onChange={(e) => setDialog((d) => ({ ...d, name: e.target.value }))}
          />
          <PhoneFields
            value={dialog?.number || ""}
            onChange={(number) => setDialog((d) => ({ ...d, number }))}
          />
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            label={tNegocio("fields.telegram")}
            value={dialog?.telegram || ""}
            onChange={(e) => setDialog((d) => ({ ...d, telegram: e.target.value }))}
            helperText={tNegocio("pages.customers.telegramHint")}
          />
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            multiline
            rows={2}
            label={tNegocio("fields.notes")}
            value={dialog?.notes || ""}
            onChange={(e) => setDialog((d) => ({ ...d, notes: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>{tNegocio("close")}</Button>
          <Button color="primary" variant="contained" onClick={saveCustomer}>
            {tNegocio("save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={importOpen}
        onClose={() => !importing && setImportOpen(false)}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        classes={{ paper: dlg.paper }}
      >
        <DialogTitle>{tNegocio("pages.customers.import.title")}</DialogTitle>
        <DialogContent dividers>
          <div className={classes.importHint}>
            <strong>{tNegocio("pages.customers.import.formatTitle")}</strong>
            <code>{tNegocio("pages.customers.import.formatSample")}</code>
            <ul>
              <li>{tNegocio("pages.customers.import.ruleName")}</li>
              <li>{tNegocio("pages.customers.import.rulePrefix")}</li>
              <li>{tNegocio("pages.customers.import.rulePhone")}</li>
              <li>{tNegocio("pages.customers.import.ruleType")}</li>
              <li>{tNegocio("pages.customers.import.ruleEmail")}</li>
              <li>{tNegocio("pages.customers.import.ruleDupes")}</li>
              <li>{tNegocio("pages.customers.import.ruleMax")}</li>
            </ul>
          </div>
          <label className={classes.importDrop}>
            <input
              type="file"
              accept=".csv,text/csv"
              hidden
              onChange={(e) => setImportFile(e.target.files?.[0] || null)}
            />
            <CloudUploadIcon color="primary" />
            <div style={{ fontWeight: 800, marginTop: 6 }}>
              {importFile ? importFile.name : tNegocio("pages.customers.import.pickFile")}
            </div>
            <div className={classes.captionBlock}>{tNegocio("pages.customers.import.dropHint")}</div>
          </label>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)} disabled={importing}>{tNegocio("close")}</Button>
          <Button
            color="primary"
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={runImport}
            disabled={importing || !importFile}
          >
            {tNegocio("pages.customers.import.action")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!detail || detailLoading}
        onClose={() => setDetail(null)}
        maxWidth="md"
        fullWidth
        scroll="paper"
        classes={{ paper: dlg.paper }}
      >
        <DialogTitle>
          <div className={classes.detailTop}>
            <div className={classes.agendaCell}>
              <ContactAvatar name={detail?.name} number={detail?.number} profilePicUrl={detail?.profilePicUrl} size={isMobile ? 56 : 96} />
              <div>
                {detail?.name || tNegocio("pages.customers.detailTitle")}
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 4 }}>
                  {detail?.code ? renderCode(detail) : null}
                  {detail ? renderType(detail) : null}
                  {detail ? <TrafficLight kind={KIND_STATUS[detail.kind] || "none"} /> : null}
                </div>
                {detail?.registeredAt ? (
                  <div className={classes.captionBlock}>
                    {tNegocio("pages.customers.registeredAt")} {formatDate(detail.registeredAt)}
                  </div>
                ) : null}
              </div>
            </div>
            {detail ? (
              <div className={`${classes.actionCluster} ${classes.detailActions}`}>
                <CustomerStoreAccess contactId={detail.contactId} />
                <Button size="small" onClick={() => setDetail(null)}>{tNegocio("close")}</Button>
                <Button
                  size="small"
                  onClick={() => {
                    startEdit(detail);
                    setDetail(null);
                  }}
                >
                  {tNegocio("edit")}
                </Button>
                {isMobile ? (
                  <Tooltip title={tNegocio("pages.customers.newTicket")}>
                    <IconButton size="small" onClick={() => openTicket(detail)}>
                      <ChatIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Button size="small" startIcon={<ChatIcon />} onClick={() => openTicket(detail)}>
                    {tNegocio("pages.customers.newTicket")}
                  </Button>
                )}
                <Button
                  size="small"
                  color="primary"
                  variant="contained"
                  startIcon={<ShoppingCartIcon />}
                  onClick={() => history.push(servicesPath(detail.contactId, true))}
                >
                  {tNegocio("new.sale")}
                </Button>
              </div>
            ) : null}
          </div>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading && !detail ? (
            tNegocio("loading")
          ) : detail ? (
            <>
              <div className={classes.captionBlock}>
                <div className={classes.phoneInline}>{renderPhone(detail) || null}</div>
                {detail.telegram ? (
                  <div className={classes.metricCell}>
                    <AlternateEmailIcon fontSize="small" className={classes.metricTelegram} />
                    {detail.telegram}
                  </div>
                ) : null}
                {detail.notes ? <div>{detail.notes}</div> : null}
              </div>
              <CustomerStats customer={detail} onOpenWallet={() => openPoints(detail)} />
              {detail.metrics ? (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, margin: "8px 0 4px", fontSize: 12 }}>
                  {[
                    [tNegocio("pages.customers.metrics.avgTicket"), detail.metrics.purchases ? money(detail.metrics.avgTicket) : "—"],
                    [
                      tNegocio("pages.customers.metrics.lastPurchase"),
                      detail.metrics.daysSinceLast == null
                        ? "—"
                        : tNegocio("pages.customers.metrics.daysAgo").replace("{n}", detail.metrics.daysSinceLast),
                    ],
                    [
                      tNegocio("pages.customers.metrics.frequency"),
                      detail.metrics.avgGapDays == null
                        ? "—"
                        : tNegocio("pages.customers.metrics.everyDays").replace("{n}", detail.metrics.avgGapDays),
                    ],
                    [
                      tNegocio("pages.customers.metrics.churn"),
                      tNegocio(`pages.customers.metrics.risk.${detail.metrics.churnRisk || "bajo"}`),
                      { alto: "#d32f2f", medio: "#ed6c02", bajo: "#2e7d32" }[detail.metrics.churnRisk || "bajo"],
                    ],
                    [
                      tNegocio("pages.customers.metrics.autoRenew"),
                      detail.autoRenewWallet ? tNegocio("pages.customers.metrics.yes") : tNegocio("pages.customers.metrics.no"),
                    ],
                  ].map(([label, value, color]) => (
                    <div
                      key={label}
                      style={{ padding: "6px 10px", borderRadius: 8, background: "rgba(127,127,127,0.08)", minWidth: 110 }}
                    >
                      <div style={{ opacity: 0.7 }}>{label}</div>
                      <div style={{ fontWeight: 800, fontSize: 14, color: color || undefined }}>{value}</div>
                    </div>
                  ))}
                </div>
              ) : null}

              <div className={classes.sectionTitle}>{tNegocio("pages.customers.services")}</div>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{tNegocio("cols.account")}</TableCell>
                    <TableCell>{tNegocio("cols.status")}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {servicePager.slice.map((s) => (
                    <TableRow
                      key={s.id}
                      hover
                      className={classes.clickableRow}
                      onClick={() => setPeekItem(s)}
                    >
                      <TableCell>
                        <ServiceAccountCell sub={s} />
                      </TableCell>
                      <TableCell>
                        <DueBadge endDate={s.endDate} status={s.status} />
                      </TableCell>
                      <TableCell align="right" className={classes.stickyActions} onClick={(e) => e.stopPropagation()}>
                        <ServiceRowActions
                          sub={s}
                          customer={detail}
                          payments={payments.records || []}
                          onEdit={(row) => setServiceDialog({ mode: "edit", sub: row })}
                          onRenew={(row) => setServiceDialog({ mode: "renew", sub: row })}
                          onNotify={notifyCustomer}
                          onDeleted={() => {
                            openDetail(detail.contactId);
                            reload({ soft: true });
                          }}
                          onChanged={() => {
                            openDetail(detail.contactId);
                            reload({ soft: true });
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                  {!(detail.subscriptions || []).length && (
                    <TableRow>
                      <TableCell colSpan={3}>{tNegocio("pages.customers.noServices")}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <NegocioPager
                count={servicePager.count}
                page={servicePager.page}
                rowsPerPage={servicePager.rowsPerPage}
                onChangePage={servicePager.setPage}
                onChangeRowsPerPage={servicePager.setRowsPerPage}
                hideBelow={10}
              />

              <div className={classes.sectionTitle}>{tNegocio("pages.customers.sales")}</div>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{tNegocio("cols.date")}</TableCell>
                    <TableCell>{tNegocio("cols.payment")}</TableCell>
                    <TableCell>{tNegocio("pages.customers.totalPurchases")}</TableCell>
                    <TableCell>{tNegocio("cols.status")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {salePager.slice.map((s) => (
                    <TableRow
                      key={s.id}
                      hover
                      className={classes.clickableRow}
                      onClick={() => setPeekSale(s)}
                    >
                      <TableCell>{formatDate(s.createdAt)}</TableCell>
                      <TableCell>{s.paymentMethod?.name || ""}</TableCell>
                      <TableCell>{money(s.total)}</TableCell>
                      <TableCell>
                        <StatusChip value={s.status} />
                      </TableCell>
                    </TableRow>
                  ))}
                  {!(detail.sales || []).length && (
                    <TableRow>
                      <TableCell colSpan={4}>{tNegocio("pages.customers.noSales")}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <NegocioPager
                count={salePager.count}
                page={salePager.page}
                rowsPerPage={salePager.rowsPerPage}
                onChangePage={salePager.setPage}
                onChangeRowsPerPage={salePager.setRowsPerPage}
                hideBelow={10}
              />
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!pointsInfo}
        onClose={closePoints}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        classes={{ paper: dlg.paper }}
      >
        <DialogTitle>
          <div className={classes.detailTop}>
            <div className={classes.agendaCell}>
              <AccountBalanceWalletIcon className={classes.metricPoints} />
              <div>
                {tNegocio("pages.customers.walletHistory")}
                <div className={classes.captionBlock}>
                  {pointsInfo?.name} · {formatWallet(pointsInfo?.walletBalance, pointsInfo?.walletCurrency)}
                </div>
              </div>
            </div>
            {pointsInfo?.contactId ? (
              <PointsSendTrigger customer={pointsInfo} disabled={pointsLoading}>
                <IconButton size="small" color="primary" aria-label={tNegocio("pages.customers.sendWallet")}><SendIcon fontSize="small" /></IconButton>
              </PointsSendTrigger>
            ) : null}
          </div>
        </DialogTitle>
        <DialogContent dividers>
          {pointsRows.some(row => !row.currency) ? <div className={classes.captionBlock} style={{ marginBottom: 8 }}>{tNegocio("pages.customers.walletHistoryCurrencyHint")}</div> : null}
          {pointsLoading && !pointsRows.length ? (
            tNegocio("loading")
          ) : pointsRows.length ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{tNegocio("cols.date")}</TableCell>
                  <TableCell>{tNegocio("pages.customers.pointsReason")}</TableCell>
                  <TableCell align="right">{tNegocio("pages.customers.walletAmount")}</TableCell>
                  <TableCell align="right">{tNegocio("pages.customers.wallet")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {walletPager.slice.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{formatDateTime(row.createdAt)}</TableCell>
                    <TableCell>
                      {tNegocio(`pages.customers.walletReasons.${row.reason}`) || row.reason}
                      {row.notes ? <div className={classes.captionBlock}>{row.notes}</div> : null}
                    </TableCell>
                    <TableCell align="right" style={{ color: Number(row.amount) < 0 ? "#c62828" : "#2e7d32", fontWeight: 600 }}>
                      {Number(row.amount) > 0 ? "+" : ""}
                      {formatWalletMovement(row.amount, row.currency)}
                    </TableCell>
                    <TableCell align="right">{formatWalletMovement(row.balanceAfter, row.currency)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            tNegocio("pages.customers.walletEmpty")
          )}
          <NegocioPager
            count={walletPager.count}
            page={walletPager.page}
            rowsPerPage={walletPager.rowsPerPage}
            onChangePage={walletPager.setPage}
            onChangeRowsPerPage={walletPager.setRowsPerPage}
            hideBelow={10}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closePoints}>{tNegocio("close")}</Button>
        </DialogActions>
      </Dialog>

      <CustomerServiceDialog
        open={!!serviceDialog}
        mode={serviceDialog?.mode || "edit"}
        sub={serviceDialog?.sub}
        customer={detail}
        onClose={() => setServiceDialog(null)}
        onSaved={onServiceSaved}
      />
      <PurchasedItemDialog
        open={!!peekItem}
        item={peekItem}
        customer={detail}
        payments={payments.records || []}
        onClose={() => setPeekItem(null)}
        onPeekAccount={setPeekAccountId}
        onChanged={() => {
          if (detail?.contactId) openDetail(detail.contactId);
          reload({ soft: true });
        }}
        onNotify={notifyCustomer}
      />
            <SendPromptDialog
        prompt={sendPrompt}
        onClose={closeSendPrompt}
        onTemplates={() => {
          closeSendPrompt();
          history.push("/negocio/configuracion/mensajes");
        }}
        onAddAnother={(prompt) => {
          const customer = prompt?.customer;
          setSendPrompt(null); // keep batchSinceRef
          if (customer) setAddItemCustomer(customer);
        }}
        onSend={async (prompt, channel) => {
          if (prompt.cart || ["order", "last", "reminder", "update", "renewed"].includes(prompt.kind)) {
            await notifyCart({
              kind: prompt.kind || "last",
              channel,
              contactId: prompt.customer?.contactId,
              subscriptionIds: prompt.subscriptionIds,
            });
          } else {
            await notifyCustomer({
              kind: prompt.kind,
              channel,
              contactId: prompt.customer?.contactId,
              subscriptionId: prompt.subscriptionId,
            });
          }
          closeSendPrompt();
        }}
      />
      <AccountPeek
        open={!!peekAccountId}
        accountId={peekAccountId}
        onClose={() => setPeekAccountId(null)}
        onChanged={() => {
          reload({ soft: true });
          if (detail?.contactId) openDetail(detail.contactId);
        }}
      />
      <SalePeek sale={peekSale} onClose={() => setPeekSale(null)} />

      <ConfirmationModal
        title={tNegocio("pages.customers.createSaleTitle")}
        open={!!salePrompt}
        onClose={() => setSalePrompt(null)}
        onConfirm={() => {
          const customer = salePrompt;
          setSalePrompt(null);
          setCartConfig(customer);
        }}
        confirmText={tNegocio("pages.customers.createSaleYes")}
        cancelText={tNegocio("pages.customers.createSaleNo")}
      >
        {tNegocio("pages.customers.createSaleBody")}
      </ConfirmationModal>
      <CartConfigDialog
        open={!!cartConfig}
        customers={cartConfig ? [cartConfig, ...records.filter((c) => Number(c.contactId) !== Number(cartConfig.contactId))] : records}
        payments={payments.records || []}
        preset={cartConfig}
        lockCustomer
        onClose={() => setCartConfig(null)}
        onSaved={async (contactId) => {
          setCartConfig(null);
          try {
            const { data } = await api.get(`/negocio/customers/${contactId}`);
            setAddProductPrompt(data);
          } catch (e) {
            toastError(e);
          }
          reload({ soft: true });
        }}
      />
      <ConfirmationModal
        title={tNegocio("pages.customers.addProductsTitle")}
        open={!!addProductPrompt}
        onClose={() => setAddProductPrompt(null)}
        onConfirm={() => {
          const customer = addProductPrompt;
          setAddProductPrompt(null);
          setAddItemCustomer(customer);
        }}
        confirmText={tNegocio("pages.customers.addProductsYes")}
        cancelText={tNegocio("pages.customers.addProductsNo")}
      >
        {tNegocio("pages.customers.addProductsBody")}
      </ConfirmationModal>
      <AddCartItemDialog
        open={!!addItemCustomer}
        customer={addItemCustomer}
        payments={payments.records || []}
        cartHasItems={!!(addItemCustomer?.subscriptions || []).length || batchSinceRef.current != null}
        onClose={() => setAddItemCustomer(null)}
        onSaved={(result) => {
          const customer = addItemCustomer;
          setAddItemCustomer(null);
          openSendLast(customer, result?.subscriptionIds || []);
          reload({ soft: true });
        }}
      />

      {chatModal}
    </>
  );
}
